import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCriticalAlerts } from '../services/alert';
import { expandAxisAlerts, getAxisLabel } from '../utils/alertStatus';
import './State.css';

const State = () => {
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isOverflowing, setIsOverflowing] = useState(false);
    const [overflowDistance, setOverflowDistance] = useState(0);
    const containerRef = useRef(null);
    const textRef = useRef(null);
    const navigate = useNavigate();

    const fetchAlerts = async () => {
        try {
            setLoading(true);
            const data = await getCriticalAlerts();
            const normalizedAlerts = Array.isArray(data)
                ? data
                : Array.isArray(data?.alerts)
                  ? data.alerts
                  : [];
            const expandedAlerts = expandAxisAlerts(normalizedAlerts);
            const withAxis = expandedAlerts.map((alert) => {
                const axisLabel = getAxisLabel(alert?.axis);
                if (!axisLabel) return alert;
                const context = alert?.context || '';
                if (context.includes(axisLabel)) return alert;
                return {
                    ...alert,
                    context: context ? `${context}（${axisLabel}）` : axisLabel,
                };
            });
            setAlerts(withAxis);
            setCurrentIndex(0);
            setError(null);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAlerts();
    }, []);

    useEffect(() => {
        // Guard against stale index if the alert list shrinks or is empty
        if (alerts.length === 0 && currentIndex !== 0) {
            setCurrentIndex(0);
        } else if (currentIndex >= alerts.length && alerts.length > 0) {
            setCurrentIndex(0);
        }
    }, [alerts, currentIndex]);

    useEffect(() => {
        if (alerts.length > 0) {
            const intervalDuration = isOverflowing ? 7000 : 5000;
            const interval = setInterval(() => {
                setCurrentIndex((prevIndex) => (prevIndex + 1) % alerts.length);
            }, intervalDuration);
            return () => clearInterval(interval);
        }
    }, [alerts, isOverflowing]);

    useEffect(() => {
        const checkOverflow = () => {
            if (
                typeof window === 'undefined' ||
                !containerRef.current ||
                !textRef.current
            ) {
                return;
            }

            const containerWidth = containerRef.current.clientWidth;
            const textElement = textRef.current;
            const previousWhiteSpace = textElement.style.whiteSpace;
            const previousWidth = textElement.style.width;

            textElement.style.whiteSpace = 'nowrap';
            textElement.style.width = 'max-content';
            const textWidth = textElement.scrollWidth;
            textElement.style.whiteSpace = previousWhiteSpace;
            textElement.style.width = previousWidth;

            const overflow = textWidth > containerWidth;
            setIsOverflowing(overflow);
            setOverflowDistance(overflow ? textWidth - containerWidth : 0);
        };

        checkOverflow();
        window.addEventListener('resize', checkOverflow);
        return () => window.removeEventListener('resize', checkOverflow);
    }, [alerts, currentIndex]);

    const handleClick = () => {
        const currentAlert = alerts[currentIndex];
        if (currentAlert?.device_name) {
            navigate(`/device/${currentAlert.device_name}`);
        } else {
            navigate('/');
        }
    };

    if (loading) return <div>Loading critical alerts...</div>;
    if (error) return <div>Error: {error}</div>;

    const currentAlert = alerts[currentIndex];
    const statusClass =
        currentAlert?.status === 'yellow'
            ? 'alert-yellow'
            : currentAlert?.status === 'red'
              ? 'alert-red'
              : '';

    return (
        <div className="state-container" ref={containerRef}>
            {alerts.length === 0 ? (
                <p>目前無警戒狀態</p>
            ) : (
                <div
                    key={currentIndex}
                    ref={textRef}
                    className={`alert-text ${statusClass} ${isOverflowing ? 'marquee' : ''}`}
                    onClick={handleClick}
                    style={{
                        cursor: 'pointer',
                        ...(isOverflowing
                            ? { '--marquee-offset': `${overflowDistance}px` }
                            : {}),
                    }}
                >
                    {currentAlert?.context || '目前無警戒狀態'}
                </div>
            )}
        </div>
    );
};

export default State;
